import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Zap, Wind, Shield, Sparkles, PlayCircle, ChevronDown } from "lucide-react";
import axios from "axios";
import { AWNING_HERO, AWNING_HERO_MAXX2M, AWNING_WALKAROUND_VIDEO } from "@/data/awnings";
import { ScrollReveal } from "@/components/ScrollReveal";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";
import { DealerStickyBar } from "@/components/DealerStickyBar";
import { BecomeADealerModal } from "@/components/BecomeADealerModal";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n.toLocaleString("en-NZ");

// Renders "**bold**" markers inside proof-point body text as bold white spans
const renderBoldSegments = (text) =>
  text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i} className="text-white font-semibold">{part}</strong> : part
  );

const PILLARS = [
  { icon: Zap, t: "Rapid Deployment", d: "Quick Pitch systems deploy in as little as 20 to 30 seconds — set up before you've unpacked the fridge." },
  { icon: Wind, t: "Strongest Freestanding Design", d: "We believe this to be the strongest freestanding awning frame on the market — built on a class-leading 6mm thick folded stainless steel hinge." },
  { icon: Shield, t: "Aluminised Canvas", d: "Reflects radiant heat and UV instead of absorbing it — naturally light-blocking too, for deeper shade and less glare." },
  { icon: Sparkles, t: "Full Ecosystem", d: "Genuine 400gsm South African woven polycotton canvas wall kits, LED lighting, rain gutters — one integrated system." },
];

// Close-up photographic evidence backing up the two hardest-to-fake claims in
// the pillars above — mirrors the "Photographic Evidence" section pattern
// used on the Wellside Canopies page.
const AWNING_PROOF_POINTS = [
  {
    image: "/products/awning-proof/two-vehicles-canopy-shapes.webp",
    objectPosition: "50% 50%",
    aspectClass: "aspect-[4/3]",
    label: "01 — Thermal Performance",
    title: "REFLECTS HEAT. DOESN'T JUST BLOCK IT.",
    body: "Most awning fabric on the market is just polyester, or lower quality grades of polycotton canvas — it blocks direct sun but still absorbs and slowly re-radiates heat underneath, the diffuse heat transfer that leaves you sweating in the shade. It also tends to let diffuse light filter straight through the weave. Quick Pitch's aluminised technical canvas works differently: a reflective aluminised layer bounces radiant heat and UV away before it ever builds up instead of soaking it in, and its density blocks that filtered light outright.",
    emphasis: "The result: fabric engineered to reflect heat rather than absorb it, and naturally light-blocking rather than merely light-filtering — so the shelter underneath runs meaningfully cooler, with deeper, truer shade.",
  },
  {
    image: "/products/awning-proof/frame-hinge-closeup.jpg",
    objectPosition: "50% 35%",
    label: "02 — Frame Engineering",
    title: "6MM STAINLESS HINGES. THE STRONGEST FREESTANDING FRAME WE KNOW OF.",
    body: "Quick Pitch awnings are built around a **6mm-thick folded stainless steel hinge** — setting the benchmark for freestanding awning strength, a spec we haven't seen matched elsewhere in the market.\n\nThere's footage from around the world of these awnings left standing freestanding through severe gale-force winds, unbraced and unguyed. We're not recommending you deploy one that way — but we believe this will be the last awning standing when others fold. Like any awning, it will eventually fail if the wind is strong enough.",
    emphasis: "This is a real benefit in NZ where you may want to have the awning open over a sandy beach or river rocks, where you can't easily peg or guy an awning.",
  },
];

// Mounting brackets for the awning — these live under the Roof Racks category/URL
// (they're engineered to clip onto a K9 rack or load bar), but shoppers browsing
// Awnings should see them too, right alongside the wall kits and rain gutter.
// Fetched live from the roofrack API (not hardcoded) so their display order stays
// in sync with whatever order they're dragged into on the Roof Racks admin list.
const AWNING_MOUNT_SLUGS = ["awning-bracket-k9-load-bars", "awning-bracket-quick-pitch", "reinforced-awning-bracket-k9-154", "universal-awning-mount-k9"];

const Card = ({ m, testid }) => (
  <Link
    to={m.to || `/awnings/${m.slug}`}
    data-testid={testid}
    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] overflow-hidden relative">
      <HoverImage primary={m.thumb} secondary={m.gallery?.[1]?.src} alt={m.name} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
      <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap">
        {m.badge && (
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
            {m.badge}
          </span>
        )}
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3 className="font-display text-xl md:text-2xl text-white leading-tight tracking-wide">{m.name.toUpperCase()}</h3>
        <p className="text-[11px] font-mono uppercase tracking-wider text-white/70 mt-2 line-clamp-1">{m.tagline}</p>
        <div className="flex items-end justify-between gap-3 mt-4">
          <div>
            {m.priceWas && <p className="font-mono text-[10px] text-white/40 line-through">${fmt(m.priceWas)}</p>}
            <p className="font-display text-2xl text-[var(--brand-primary)] leading-none">
              {m.priceFrom && <span className="text-[10px] font-mono uppercase tracking-widest text-white/60 mr-1">FROM</span>}
              ${fmt(m.price)}
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
            View <ArrowUpRight size={12} />
          </span>
        </div>
      </div>
    </div>
  </Link>
);

// Extracts YouTube ID from any common URL format
const ytId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
};

export default function AwningsPage() {
  const videoId = ytId(AWNING_WALKAROUND_VIDEO);
  const [products, setProducts] = useState(null); // null = still loading
  const [roofrackMounts, setRoofrackMounts] = useState([]);
  const [expandedProof, setExpandedProof] = useState({});
  const [dealerModalOpen, setDealerModalOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/products/awning`).then((res) => setProducts(res.data)).catch((err) => { console.error(err); setProducts([]); });
    axios.get(`${API}/api/products/roofrack`).then((res) => {
      setRoofrackMounts(res.data.filter((p) => AWNING_MOUNT_SLUGS.includes(p.slug)).map((p) => ({ ...p, to: `/roof-racks/${p.slug}` })));
    }).catch((err) => console.error(err));
  }, []);

  const models = (products || []).filter((m) => m.type === "awning");
  const accessories = [...(products || []).filter((m) => m.type === "accessory"), ...roofrackMounts];

  if (products === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  return (
    <div data-testid="awnings-page">
      <DealerStickyBar onOpenModal={() => setDealerModalOpen(true)} testIdPrefix="awnings" />
      <BecomeADealerModal
        open={dealerModalOpen}
        onOpenChange={setDealerModalOpen}
        productLabel="Quick Pitch awnings"
        testId="awnings-dealer-modal"
      />
      <SEO
        path="/awnings"
        title="Quick Pitch & EEZI-AWN Freestanding Awnings NZ"
        description="South African aluminised technical canvas awnings — Quick Pitch 270°, EEZI-AWN BAT270, DragonFly and full accessories. Built for real overland weather. Regular 270 & Maxx 2m 270 layouts."
        image={AWNING_HERO}
      />

      {/* HERO 1 — Regular 270 Layout. Mobile: solid dark strip behind the
      fixed header (like the Wellside Canopies page) instead of the hero
      image running full-bleed under it — image starts right below instead. */}
      <section className="relative overflow-hidden md:min-h-[100svh] md:flex md:items-end">
        <div className="md:hidden h-20 bg-[var(--bg-main)]" data-testid="awnings-hero-mobile-header-strip" />
        <div className="relative h-[38vh] md:absolute md:inset-0 md:h-auto">
          <img src={AWNING_HERO} alt="Regular 270 Layout awning" className="w-full h-full object-cover object-[35%_center] md:object-[60%_center]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-12% via-black/20 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-6 md:pb-20 pt-6 md:pt-32 w-full">
          <p className="overline mb-4 md:mb-6">— Quick Pitch · EEZI-AWN · Freestanding Awnings</p>
          {/* H1 + badge + intro + CTAs stay on the hero image on desktop only — they move below on mobile */}
          <div className="hidden md:block">
            <h1 className="font-display text-[9vw] lg:text-[7.5vw] leading-[0.85] text-white hero-text-shadow max-w-[14ch]">
              THE BENCHMARK<br />
              IN <span className="text-[var(--brand-primary)]">FREESTANDING</span><br />
              AWNINGS.
            </h1>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--brand-primary)] bg-black/60 backdrop-blur-sm px-3 py-1.5 border border-[var(--brand-primary)]/40">
                Regular 270° Layout
              </span>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-10 items-end">
              <p className="text-base md:text-lg text-white/85 leading-relaxed max-w-xl">
                A noticeably cooler shelter. Advanced aluminised canvas reflects heat and UV. Genuine 400gsm South African polycotton wall kits available.
              </p>
              <div className="flex flex-wrap gap-3 justify-end">
                <a href="#range" data-testid="awnings-cta-range" className="btn-primary">View Range <ArrowUpRight size={14} /></a>
                <a href="#enquire" data-testid="awnings-cta-enquire" className="btn-secondary">Enquire</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile only: H1 + badge + CTAs sit below the hero image on the plain dark background, ahead of the supporting paragraph */}
      <section className="md:hidden bg-[var(--bg-main)] container-x pt-4 pb-6" data-testid="awnings-hero-mobile-intro">
        <h1 className="font-display text-[10vw] leading-[0.85] text-white max-w-[14ch] mb-4">
          THE BENCHMARK<br />
          IN <span className="text-[var(--brand-primary)]">FREESTANDING</span><br />
          AWNINGS.
        </h1>
        <span className="inline-block font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--brand-primary)] bg-black/60 px-3 py-1.5 border border-[var(--brand-primary)]/40 mb-4">
          Regular 270° Layout
        </span>
        <div className="flex flex-wrap gap-3 mb-4">
          <a href="#range" data-testid="awnings-cta-range-mobile" className="btn-primary">View Range <ArrowUpRight size={14} /></a>
          <a href="#enquire" data-testid="awnings-cta-enquire-mobile" className="btn-secondary">Enquire</a>
        </div>
        <p className="text-base text-white/85 leading-relaxed">
          A noticeably cooler shelter. Advanced aluminised canvas reflects heat and UV. Genuine 400gsm South African polycotton wall kits available.
        </p>
      </section>


      {/* HERO 2 — Maxx 2m 270 Layout (scroll reveal). Mobile height matches
      HERO 1's shorter mobile ratio so the awning's full width stays visible
      instead of being cover-cropped down to just the vehicle — heading/copy
      move below on mobile, same pattern as HERO 1. */}
      <ScrollReveal
        src={AWNING_HERO_MAXX2M}
        alt="Maxx 2m 270 Layout awning"
        className="h-[42svh] md:h-[95svh] flex items-end"
        objectPosition="50% center"
        mobileObjectPosition="48% 25%"
        overlay={
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-12% via-black/20 via-45% to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
          </>
        }
      >
        <div className="hidden md:block relative z-10 container-x pb-14 md:pb-20 pt-16 w-full">
          <span className="inline-block font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--brand-primary)] bg-black/60 backdrop-blur-sm px-3 py-1.5 border border-[var(--brand-primary)]/40 mb-6">
            Maxx 2m 270° Layout
          </span>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.9] text-white hero-text-shadow max-w-[20ch]">
            COMPARE THE REGULAR 270 SHAPE<br />
            TO THE <span className="text-[var(--brand-primary)]">2M MAXX</span> SHAPE.
          </h2>
          <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/85 max-w-xl leading-relaxed mt-6 [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
            Same superior quality, similar total coverage. Same aluminised technical canvas, same 400gsm South African polycotton wall kit compatibility, same freestanding capability. Choose the shape that suits your vehicle and how you camp.
          </p>
        </div>
      </ScrollReveal>

      {/* Mobile only: badge + heading + copy sit below the now-shorter, uncropped hero image */}
      <section className="md:hidden bg-[var(--bg-main)] container-x pt-6 pb-4" data-testid="awnings-maxx2m-mobile-copy">
        <span className="inline-block font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--brand-primary)] bg-black/60 px-3 py-1.5 border border-[var(--brand-primary)]/40 mb-4">
          Maxx 2m 270° Layout
        </span>
        <h2 className="font-display text-[9vw] leading-[0.9] text-white max-w-[16ch] mb-4">
          COMPARE THE REGULAR 270 SHAPE<br />
          TO THE <span className="text-[var(--brand-primary)]">2M MAXX</span> SHAPE.
        </h2>
        <p className="text-base text-white/85 leading-relaxed">
          Same superior quality, similar total coverage. Same aluminised technical canvas, same 400gsm South African polycotton wall kit compatibility, same freestanding capability. Choose the shape that suits your vehicle and how you camp.
        </p>
      </section>

      {/* WHY QUICK PITCH */}
      <section className="container-x pt-10 pb-10 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <p className="overline mb-4">— Advanced Technical Canvas</p>
            <h2 className="font-display text-3xl md:text-5xl leading-[0.95] text-white">
              NOT ALL CANVAS<br />IS <span className="text-[var(--brand-primary)]">EQUAL.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4 text-[var(--text-secondary)] text-base md:text-lg leading-relaxed">
            <p>
              Quick Pitch South Africa awnings use advanced aluminised technical canvas that reflects significantly more radiant heat than conventional awning fabrics — the difference is most noticeable on hot, high-UV days, when other awnings leave you sweating in the shade.
            </p>
            <p>
              The fabric is also naturally light-blocking, producing deeper shade and reducing glare throughout the day. Pair that with the matching 400gsm South African polycotton wall kits and you get a level of comfort, weather protection, and durability rarely found in the New Zealand market.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS — horizontal swipe strip on mobile (less scroll), grid on desktop */}
      <section className="container-x pb-10 md:pb-16">
        <div className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-px overflow-x-auto md:overflow-visible pb-2 md:pb-0 -mx-1 px-1 md:mx-0 md:px-0 snap-x snap-mandatory md:snap-none scrollbar-thin md:bg-[var(--border-default)] md:border md:border-[var(--border-default)]">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} data-testid={`awnings-pillar-${i}`} className="shrink-0 w-[78vw] sm:w-[300px] md:w-auto snap-start border border-[var(--border-default)] md:border-0 bg-[var(--bg-main)] p-6 md:p-8">
                <Icon size={26} className="text-[var(--brand-primary)] mb-5" strokeWidth={1.3} />
                <h3 className="font-display text-xl text-white tracking-wide">{p.t}</h3>
                <p className="text-[var(--text-secondary)] text-sm mt-3 leading-relaxed">{p.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* PHOTOGRAPHIC EVIDENCE */}
      <section className="container-x pt-6 pb-10 md:pb-20">
        <div className="mb-10 max-w-2xl">
          <p className="overline mb-4">— See It Up Close</p>
          <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
            THE DETAILS THAT MAKE IT <span className="text-[var(--brand-primary)]">CLASS-LEADING.</span>
          </h2>
        </div>
        <div className="space-y-16 md:space-y-20">
          {AWNING_PROOF_POINTS.map((p, i) => (
            <div
              key={i}
              data-testid={`awning-evidence-point-${i}`}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
            >
              <div className="lg:col-span-5">
                <div className={`relative ${p.aspectClass || "aspect-[4/3]"} overflow-hidden border border-[var(--border-default)] bg-[var(--bg-tertiary)]`}>
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: p.objectPosition || "center" }}
                  />
                </div>
              </div>
              <div className="lg:col-span-7">
                <p className="font-mono text-xs tracking-[0.3em] uppercase text-[var(--brand-primary)] mb-4">{p.label}</p>
                <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">{p.title}</h3>
                <div className={`${expandedProof[i] ? "block" : "hidden md:block"}`}>
                  {p.body.split(/\n\n+/).map((para, pi, arr) => (
                    <p key={pi} className="text-[var(--text-secondary)] leading-relaxed text-lg mt-6">
                      {renderBoldSegments(para)}
                      {pi === arr.length - 1 && p.emphasis && <span className="text-white font-semibold"> {p.emphasis}</span>}
                    </p>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setExpandedProof((prev) => ({ ...prev, [i]: !prev[i] }))}
                  className="md:hidden mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[var(--brand-primary)] hover:text-white transition-colors"
                  data-testid={`awning-evidence-point-toggle-${i}`}
                >
                  {expandedProof[i] ? "Read Less" : "Read More"}
                  <ChevronDown size={14} className={`transition-transform ${expandedProof[i] ? "rotate-180" : ""}`} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WALKAROUND VIDEO */}
      <section className="border-y border-[var(--border-default)] bg-[var(--bg-secondary)]">
        <div className="container-x py-10 md:py-20">
          <div className="mb-8">
            <p className="overline mb-3">— Product Walk-Around</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              SEE THE AWNINGS <span className="text-[var(--brand-primary)]">IN THE FLESH.</span>
            </h2>
          </div>
          <div className="aspect-video overflow-hidden border border-[var(--border-default)] bg-black relative">
            {videoId ? (
              <iframe
                data-testid="awnings-video"
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                title="Xpeditionz Awnings Walk-Around"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : (
              <div data-testid="awnings-video-placeholder" className="w-full h-full flex flex-col items-center justify-center text-center p-8">
                <PlayCircle size={64} className="text-[var(--brand-primary)] mb-6" strokeWidth={1.2} />
                <p className="font-display text-2xl md:text-3xl text-white mb-2">Walk-Around Video Coming Soon</p>
                <p className="text-[var(--text-secondary)] text-sm max-w-md">
                  Recently filmed walk-around of the Quick Pitch awning range. Send us the YouTube link and we'll drop it in.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* AWNING PRODUCTS GRID */}
      <section id="range" className="container-x pt-10 pb-10 md:py-20 scroll-mt-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline mb-3">— The Range</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              AWNING <span className="text-[var(--brand-primary)]">SYSTEMS.</span>
            </h2>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)]">Prices GST inc.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {models.map((m, i) => <Card key={m.slug} m={m} testid={`awning-${i}`} />)}
        </div>
      </section>

      {/* ACCESSORIES */}
      <section className="container-x pb-10 md:pb-20 border-t border-[var(--border-default)] pt-10 md:pt-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline mb-3">— Accessories</p>
            <h2 className="font-display text-3xl md:text-4xl text-white leading-[0.95]">
              WALL KITS · MOUNTS · <span className="text-[var(--brand-primary)]">LIGHTING & GUTTERS.</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {accessories.map((m, i) => <Card key={m.to || m.slug} m={m} testid={`awning-accessory-${i}`} />)}
        </div>
      </section>
    </div>
  );
}
